import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { generateClient } from '@aws-amplify/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as icons_solid from '@fortawesome/free-solid-svg-icons';
import { getFiles } from '@/graphql/queries';
import { createFolder, deleteFile, uploadFile } from '@/graphql/mutations';
import { launchAgent as _launchAgent } from '@/graphql/mutations';
import ErrorModal from '@/ErrorModal';
import FileExplorer from '@/components/FileExplorer';

const client = generateClient();

const recordingIcon = (<FontAwesomeIcon className="fg-purple" icon={icons_solid.faVolumeLow} size="lg" />);
const transcriptIcon = (<FontAwesomeIcon className="fg-purple" icon={icons_solid.faQuoteLeft} size="lg" />);

export default ({ deal_id, subject_id }) => {
	const isConstructed = useRef(false);
	const [busy, setBusy] = useState({});
	const updateBusy = useCallback((id, value) => {
		setBusy(prevState => ({ ...prevState, [id]: value }));
	}, []);
	const [errorMessage, setErrorMessage] = useState();
	const [files, setFiles] = useState();
	const [modal, setModal] = useState();
	const [path, setPath] = useState([]);

	const catchError = error => {
		if (error instanceof Error) {
			setErrorMessage(error.toString());
		} else {
			setErrorMessage((error.errors || []).map(e => e.message?.toString()));
		}
		setBusy({});
	};

	const load = useCallback(async () => {
		try {
			updateBusy('files', true);
			const response = await client.graphql({ query: getFiles, variables: { deal_id, key: path.join('/'), subject_id } });
			setFiles(response.data.getFiles.files || []);
			updateBusy('files', false);
		} catch (error) {
			catchError(error);
		}
	}, [deal_id, JSON.stringify(path), subject_id]);

	if (!isConstructed.current) {
		isConstructed.current = true;
		load();
	}

	useEffect(() => {
		load();
	}, [deal_id, load, JSON.stringify(path), subject_id]);

	const onCreateFolder = useCallback(async key => {
		try {
			updateBusy('files', true);
			await client.graphql({ query: createFolder, variables: { input: { deal_id, key, subject_id } } });
			await load();
			updateBusy('files', false);
		} catch (error) {
			catchError(error);
		}
	}, [load]);

	const onDelete = useCallback(async file => {
		try {
			updateBusy('files', true);
			// Deleting a folder always needs / at the end
			const _key = file.is_folder ? file.key + '/' : file.key;
			await client.graphql({ query: deleteFile, variables: { input: { deal_id, key: _key, subject_id } } });
			await load();
			updateBusy('files', false);
		} catch (error) {
			catchError(error);
		}
	}, [load, path]);

	const files_el = useMemo(() => {
		if (!files) return busy.files ? (
			<div className="text-center" style={{marginBottom:'2rem',marginTop:'2rem'}}>
				<button className="btn btn-primary" type="button" disabled>
					<span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
					Loading...
				</button>
			</div>
		) : undefined;
		return (
			<div className="mt-3">
				<FileExplorer busy={busy} files={files} onChangePath={setPath} onCreateFolder={onCreateFolder} onDelete={onDelete} />
			</div>
		);
	}, [files, JSON.stringify(busy)]);

	const setFile = useCallback(async event => {
		try {
			event.persist();
			const files = Array.from(event.target.files);
			console.log('files', files);
			updateBusy('files', true);
			const _path = path.join('/') + '/';
			for await (let file of files) {
				const response = await client.graphql({ query: uploadFile, variables: { input: { deal_id, key: _path + file.name, subject_id } } });
				console.log('response', response);
				const _response = response.data.uploadFile;
				// Upload the file
				const upload_response = await fetch(_response.upload_url, {
					method: 'PUT',
					headers: {
						'Content-Type': file.type
					},
					body: file
				});
				if (upload_response.ok) {
					console.log(`Upload ${file.name} successful!`, upload_response);
				} else {
					console.error(`Upload ${file.name} failed`, upload_response);
				}
			}
			await load();
			updateBusy('files', false);
		} catch (error) {
			event.target.value = '';
			catchError(error);
		}
	}, [path]);

	return (
		<>
			{modal}
			{errorMessage && <ErrorModal message={errorMessage} onClose={() => setErrorMessage()} title="Error" />}
			<div className="row">
				<div className="col-auto ms-auto">
					<input
						className="d-none"
						id="uploadFile"
						multiple
						onChange={setFile}
						title="Upload a File"
						type="file"
					/>
					<label htmlFor="uploadFile">
						<span className="btn btn-secondary">Upload a File</span>
					</label>
				</div>
			</div>
			{files_el && (
				<div className="mb-4">
					{files_el}
				</div>
			)}
		</>
	);
};
